'use client';

import { toastsStore } from '../stores/toasts-store';

import { useStore } from './use-store';

import type { ToastInStore} from '../stores/toasts-store';
import type { ReactNode } from 'react';

type PushOptions = {
  title: string;
  text?: ReactNode;
  duration?: number;
};

type PushOptionsOrMessage = PushOptions | string;

type PushHandler = (options: PushOptionsOrMessage) => ToastRemoveHandler;

type UseToastsReturnValue = {
  push: PushHandler;
};

export type ToastRemoveHandler = () => void;

/**
 * Transforms {@link PushOptionsOrMessage} to a {@link ToastInStore}.
 */
const pushOptionsToToast = (options: PushOptionsOrMessage): ToastInStore => {
  // Transform a string to the simplest possible PushOptions object.
  if (typeof options === 'string') {
    options = {
      title: options,
    };
  }

  // Destruct the options and assign default values.
  const { text, title, duration = 3000 } = options;

  // Let's try to create a somewhat unique ID.
  const id = Date.now() + Math.round(Math.random() * 999);

  return {
    id,
    text,
    title,
    duration,
  };
};

/**
 * Returns helpers for managing toast messages in the toast store.
 *
 * ### Push a Message
 *
 * In order to push a message invoke the returned `push()` function.
 *
 * ```tsx
 * const { push } = useToasts();
 *
 * // The most basic example -- pushes only a toast with a title.
 * push('Please verify your email address before you attempt to sign in.');
 *
 * push({
 *   title: 'Your account has been created',
 *   text: 'Please verify your email address before you attempt to sign in.',
 * });
 *
 * // This toast message disappears after 10 seconds.
 * push({
 *   title: 'Your account has been created',
 *   text: 'Please verify your email address before you attempt to sign in.',
 *   duration: 10000,
 * });
 * ```
 *
 * ### Remove a Pushed Message
 *
 * You can remove a pushed message by invoking the return value of the `push()` function.
 *
 * ```tsx
 * // This toast message stays visible until removed.
 *
 * const removeMessage = push({
 *   title: 'Your account has been created',
 *   text: 'Please verify your email address before you attempt to sign in.',
 *   duration: Infinity,
 * });
 *
 * // Let's remove it manually after 5 seconds.
 * setTimeout(() => {
 *   removeMessage();
 * }, 5000);
 * ```
 */
export const useToasts = (): UseToastsReturnValue => {
  // Get the current toasts & the SetToastsAction
  const [, setToasts] = useStore(toastsStore);

  return {
    push: options => {
      // Get a Toast from the options
      const toast = pushOptionsToToast(options);

      // Push the toast to the current stack of toasts.
      setToasts(prev => [...prev, toast]);

      // Return the function to remove the toast.
      return () => {
        setToasts(prev => prev.filter(({ id }) => id !== toast.id));
      };
    },
  };
};
